import React, {useEffect, useRef} from 'react';
import {DataTableContext} from './DataTableContext';

const DragTableContext = React.createContext();

const sidebarShadow = '-3px 0px 10px rgba(36,0,48,0.14)';
const lineNumberShadow = '3px 0px 10px rgba(36,0,48,0.14)';

// reducer
const reducer = (state, action) => {
  switch(action.type) {
    case 'dragging':
    return({
      ...state,
      dragging: action.value
    })
    case 'click':
    return({
      ...state,
      mouseDown: action.value,
      dragging: state.mouseDown ? false : state.dragging,
      from: action.from,
    })
    case 'distanceUpdate':
    const distance = state.distance + action.value;
    const atStart = distance >= 0;
    const atEnd = distance <= action.widthDiff;
    const scrollbarValue = (distance / action.widthDiff) * 100 + '%';
    window.requestAnimationFrame((timestamp) => {
      const scrollbar = document.getElementById(action.id + '_scrollbar');
      const cells = document.getElementById(action.id + '_parent_cells');
      const headers = document.getElementById(action.id + '_parent_headers');
      const lineCol = document.getElementById(action.id + '_lineCol');
      const sidebar = document.getElementById(action.id + '_sidebar');
      const style = 'translateX(' +  (atStart ? 0 : atEnd ? action.widthDiff : distance) + 'px)';
      if (scrollbar) {
        scrollbar.style.left = scrollbarValue;
        scrollbar.style.transform = `translateX(${'-' + scrollbarValue})`;
      }
      if (cells) cells.style.transform = style;
      if (headers) headers.style.transform = style;
      if (atStart !== state.atStart && lineCol)
        lineCol.style.boxShadow = !atStart ? lineNumberShadow : 'none';
      if (atEnd !== state.atEnd && sidebar)
        sidebar.style.boxShadow = !atEnd ? sidebarShadow : 'none';
    });
    if (atEnd !== state.atEnd || atStart !== state.atStart || distance !== state.distance || scrollbarValue !== state.scrollbarValue) {
      return({
        ...state,
        atStart: atStart,
        atEnd: atEnd,
        distance: state.distance + action.value,
        scrollbarValue: scrollbarValue || 0,
      })
    }
    case 'initial':
    return({
      ...state,
      ...action.value,
    })

    default: return state;
  }
};


// provider
const DragTableContextProvider = props => {
  const {controller} = React.useContext(DataTableContext);

  const [state, dispatch] = React.useReducer(reducer, {
    distance: 0,
    mouseDown: false,
    dragging: false,
    atStart: true,
    atEnd: false,
    draggable: false,
    from: null,
    hover: false,
    scrollbarValue: 0,
  });
  const start = useRef();
  const timeout = useRef(null);
  useEffect(() => {clearTimeout(timeout.current)});
  const initialCheck = useRef(false);
  const scrollbarMouseDownEvent = (e) => {
    start.current = e.clientX;
    dispatch({type: 'click', value: true, from: 'scrollbar'})
  }
  const mouseDownEvent = e => {
    start.current = e.clientX;
    dispatch({type: 'click', value: true, from: 'table'})
    // document.addEventListener('mouseup', mouseUpEvent);
  }
  const mouseUpEvent = e => {
    start.current = null;
    dispatch({type: 'click', value: false});
    // document.removeEventListener('mouseup', mouseUpEvent);
  }
  const mouseOutEvent = e => {
    if (state.mouseDown) {
      start.current = null;
      dispatch({type: 'initial', value: {dragging: false, mouseDown: false}});
    }
  }
  const refreshDrag = () => {
    initialCheck.current = false;
    dispatch({type: 'inital', value: {distance: 0}})
  };

  if (
    controller && controller.data && controller.headersTotalWidth
    && controller.tableWidth && !initialCheck.current
  ) {
    initialCheck.current = true;
    const cellWidth = controller.headersTotalWidth;
    const widthDiff = (controller.tableWidth - 45) - cellWidth;
    const atEndTest = controller.data.length > 1
    ? (widthDiff === 0 || widthDiff === 1) : true;
    // if (!atEndTest) {
      document.getElementById(props.dataTableID + '_sidebar')
      .style.boxShadow = !atEndTest ? sidebarShadow : 'none';
    // }
    dispatch({type: 'initial', value: {atEnd: atEndTest, draggable: controller.data.length > 0 ? widthDiff !== 0 : false}})
  }

  const dragEvent = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (state.draggable) {
      const clientX = state.from === 'table' ? e.clientX : Math.floor((e.clientX * -1));

      let delta = clientX - start.current;
      if (state.mouseDown && state.dragging) {
        let cellWidth = controller.headersTotalWidth;
        const widthDiff = (controller.tableWidth - 45) - cellWidth;
        if (delta + state.distance <= 0 && delta + state.distance >= widthDiff)
          dispatch({type: 'distanceUpdate', value: delta, id: props.dataTableID, widthDiff: widthDiff});
        start.current = clientX;
      } else if (state.mouseDown) {
        if (delta > 5 || delta < -5) {
          dispatch({type: 'dragging', value: true})
          start.current = clientX;
        }
      }
      if (!state.hover) {
        dispatch({type: 'initial', value: {hover: true}})
      } else if (state.hover) {
        clearTimeout(timeout.current);
        timeout.current = setTimeout(() => {
          dispatch({type: 'initial', value: {hover: false}})
        }, 600);
      }
    }

  }


  return(
    <DragTableContext.Provider
      value={{
        draggable: state.draggable,
        dragDistance: state.distance,
        dragging: state.dragging,
        dragHover: state.hover,
        scrollbarValue: state.scrollbarValue,
        mouseDownEvent,
        scrollbarMouseDownEvent,
        mouseUpEvent,
        dragEvent,
        mouseOutEvent,
        refreshDrag,
      }}
    >
      {props.children}
    </DragTableContext.Provider>
  )
}

const DragTableContextConsumer = DragTableContext.Consumer;

export {DragTableContext, DragTableContextProvider, DragTableContextConsumer};
